import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add Bearer token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const res = await axios.post(`${API_URL}/api/auth/refresh`, 
            { refresh_token: refreshToken },
            { withCredentials: true }
          );
          if (res.data.access_token) {
            localStorage.setItem('access_token', res.data.access_token);
            originalRequest.headers.Authorization = `Bearer ${res.data.access_token}`;
            return api(originalRequest);
          }
        }
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(error);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper to format API errors
export function formatApiError(error) {
  const detail = error.response?.data?.detail;
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Assessment
export const getAssessmentQuestions = () => api.get('/assessment/questions');
export const submitAssessment = (answers) => api.post('/assessment/submit', { answers });
export const getAssessmentHistory = () => api.get('/assessment/history');

// Training
export const getTrainingModules = () => api.get('/training/modules');
export const updateTrainingProgress = (moduleId) => api.post(`/training/progress/${moduleId}`);

// Reports
export const submitRiskReport = (report) => api.post('/reports/submit', report);
export const getRiskReports = () => api.get('/reports');

// Chat
export const sendChatMessage = (message, sessionId) => api.post('/chat', { message, session_id: sessionId });

// Subscription
export const getSubscription = () => api.get('/subscription');

// Users
export const getDistrictUsers = () => api.get('/users');
export const addTeamMember = (data) => api.post('/team/members', data);
export const removeTeamMember = (id) => api.delete(`/team/members/${id}`);

// Campuses
export const getCampuses = () => api.get('/campuses');
export const addCampus = (data) => api.post('/campuses', data);
export const removeCampus = (id) => api.delete(`/campuses/${id}`);

// Roadmap / Action Tracker
export const getLatestRoadmap = () => api.get('/roadmap/latest');
export const generateRoadmap = () => api.post('/roadmap/generate');
export const toggleRoadmapAction = (id) => api.put(`/roadmap/action/${id}/toggle`);
export const updateActionMeta = (id, meta) => api.put(`/roadmap/action/${id}/meta`, meta);
export const addCustomAction = (data) => api.post('/roadmap/action/custom', data);
export const runDriftCheck = () => api.post('/roadmap/drift');

// Incidents
export const getIncidentMeta = () => api.get('/incidents/meta');
export const getIncidents = (params) => api.get('/incidents', { params });
export const createIncident = (data) => api.post('/incidents', data);
export const updateIncidentStatus = (id, data) => api.put(`/incidents/${id}/status`, data);
export const getIncidentAnalytics = () => api.get('/incidents/analytics');
export const detectIncidentPatterns = () => api.post('/incidents/patterns');

// Documents
export const uploadDocumentChunk = (formData) => api.post('/documents/upload-chunk', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const scoreDocument = (data) => api.post('/documents/score', data, { timeout: 180000 });
export const applyDocumentScores = (data) => api.post('/documents/apply', data);

// Evidence pack
export const downloadEvidencePack = async () => {
  const res = await api.get('/evidence-pack/export', { responseType: 'blob' });
  const url = window.URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `ERIC_Insurance_Evidence_Pack_${new Date().toISOString().slice(0, 10)}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default api;
